import { PossibleContactHealthStatusSources, PossibleContactRegistrationSources } from "@/protocols/clientCatalog"
import { WABAChannelChatCommunicationPermissionStatusOnFilter } from "@/@integrations/WABA/protocols/wabaChannelChatCommunicationPermission"
import { WABAChannelChatFreeEntryPointWindowStatusOnFilter } from "@/@integrations/WABA/protocols/wabaChannelChatFreeEntryPointWindow"

export type ContactOpportunityStatus = "won" | "lost"

export type ConditionOperator = "between" | "equal" | "not_equal" | "in" | "not_in" | "greater_than" | "less_than"

export type MainOperatorInFilter = "and" | "or"

export type AcceptedRegistrationSources = Extract<PossibleContactRegistrationSources, "message_blast" | "integration" | "manual" | "webhook" | "file_import" | "message-received" | "active_campaign_automation_block">

export type FilterTypeToFilterData = {
	contact_register_date: DefaultDateFilter
	contact_update_date: DefaultDateFilter
	contact_tag: {
		tag_id: string
	}
	contact_last_message_date: DefaultDateFilter
	contact_registered_from: {
		registered_from: AcceptedRegistrationSources
	}
	client_catalog_contacts_created_by_contacts_import: {
		client_catalog_contacts_created_by_contacts_import_id: number
	}
	client_catalog_contacts_updated_by_contacts_import: {
		client_catalog_contacts_updated_by_contacts_import_id: number
	}
	contact_health: {
		status: PossibleContactHealthStatusSources
	}
	contact_board: {
		contact_board_id: number
		contact_board_stage_id?: number
		contact_value_from_in_cents?: number
		contact_value_to_in_cents?: number
		contact_opportunity_status?: ContactOpportunityStatus
		contact_loss_reason_id?: number
	}
	contact_trackable_links: {
		contact_trackable_links_id: number
		slug?: ContactTrackableLinkFilterType
	}
	contact_account_manager: {
		account_manager_user_id: number
	}
	contact_waba_communication_permission: {
		status: WABAChannelChatCommunicationPermissionStatusOnFilter
	}
	contact_meta_free_entry_point_window: {
		status: WABAChannelChatFreeEntryPointWindowStatusOnFilter
	}
}

export type FilterType = keyof FilterTypeToFilterData

export type ContactsFiltersConditions<Type extends FilterType> = {
	operator: ConditionOperator
	type: Type
	filterData: FilterTypeToFilterData[Type]
	extraFilter?: ContactsExtraFiltersConditions<Type>
}

export type ContactsExtraFiltersConditions<Type extends FilterType> = {
	operator: ConditionOperator
	filterData: FilterTypeToExtraFilterData[Type]
}

export type FilterTypeToExtraFilterData = {
	contact_register_date: Record<string, unknown>
	contact_update_date: Record<string, unknown>
	contact_tag: DefaultDateFilter & {
		action: ExtraFilterActions | undefined
	}
	contact_last_message_date: Record<string, unknown>
	contact_registered_from: Record<string, unknown>
	client_catalog_contacts_created_by_contacts_import: Record<string, unknown>
	client_catalog_contacts_updated_by_contacts_import: Record<string, unknown>
	contact_health: Record<string, unknown>
	contact_board: Record<string, unknown>
	contact_trackable_links: Record<string, unknown>
	contact_account_manager: Record<string, unknown>
	contact_waba_communication_permission: Record<string, unknown>
	contact_meta_free_entry_point_window: Record<string, unknown>
}

export type ExtraFilterActions = "associated" | "disassociated"

export type GetQueryOperatorText = {
	columnName: "contact_import_history_id" | "last_message_transacted_date" | "created_at" | "\"contacts\".\"data\"->>'registered_from'" | "\"ClientCatalogClientModel\".\"id\"" | "updated_at" | "deleted_at" | "contact_exist_in_whatsapp" | "account_manager_user_id"
	firstParam: string
	secondParam: string
	listParam: string[]
}

export type ContactTrackableLinkFilterType = "contact_clicked_and_sent_message"

export type ContactFilters = {
	mainOperator?: MainOperatorInFilter
	title?: string
}

export type BaseContactsFiltersType = ContactFilters & {
	id?: number
	inboxChannelId?: number
	conditions?: ContactsFiltersConditions<keyof FilterTypeToFilterData>[]
}

type DefaultDateFilter = {
	dateFrom?: Date
	dateTo?: Date
}

export type DateFilterState = DefaultDateFilter & {
	operator: ConditionOperator
}

export const defaultDateOperators = [
	{ label: "Entre", value: "between" },
	{ label: "Após", value: "greater_than" },
	{ label: "Antes", value: "less_than" }
]

export type DateFilterOptionsProps = DateFilterState & {
	onChange: (condition: ContactsFiltersConditions<FilterType>) => void
}
